import { apiClient } from '@/utils/api-client';
import type { ApiResponse, JsonApiResource } from '@/types/api';

export interface TriggerConfig {
  patterns?: string[];
  keywords?: string[];
  semantic_examples?: string[];
  negative_patterns?: string[];
}

export interface SkillTemplate {
  id: string;
  name: string;
  description: string;
  display_title: string;
  skill_md_content: string;
  scripts: Record<string, string>;
  references: Record<string, string>;
  metadata: Record<string, unknown>;
  skill_type: 'interactive' | 'template' | 'passive';
  execution_mode: 'conversational' | 'guided' | 'deliverable';
  trigger_config: TriggerConfig;
  execution_config: Record<string, unknown>;
  document_skills: string[];
  has_guided_steps: boolean;
  is_active: boolean;
  current_version_number: number | null;
  provider_sync_status: ProviderSyncStatus[];
  version_count: number;
  created_at: string;
  updated_at: string;
}

export interface ProviderSyncStatus {
  provider: string;
  sync_status: 'pending' | 'synced' | 'error';
  last_synced_at: string | null;
  error_message: string | null;
}

export interface SkillTemplateVersion {
  id: string;
  version_number: number;
  change_description: string | null;
  created_by: string | null;
  created_at: string;
}

export interface SkillTemplateCreateData {
  name: string;
  description: string;
  display_title: string;
  skill_md_content: string;
  skill_type: SkillTemplate['skill_type'];
  execution_mode?: SkillTemplate['execution_mode'];
  trigger_config?: TriggerConfig;
  execution_config?: Record<string, unknown>;
  document_skills?: string[];
  scripts?: Record<string, string>;
  references?: Record<string, string>;
  metadata?: Record<string, unknown>;
}

export interface SkillTemplateUpdateData {
  name?: string;
  description?: string;
  display_title?: string;
  skill_md_content?: string;
  skill_type?: SkillTemplate['skill_type'];
  execution_mode?: SkillTemplate['execution_mode'];
  trigger_config?: TriggerConfig;
  execution_config?: Record<string, unknown>;
  document_skills?: string[];
  is_active?: boolean;
  scripts?: Record<string, string>;
  references?: Record<string, string>;
  metadata?: Record<string, unknown>;
}

export interface GenerateTriggersParams {
  description: string;
  skill_name?: string;
  skill_description?: string;
}

export interface GenerateTriggersResult {
  keywords: string[];
  patterns: string[];
  negative_patterns: string[];
}

export interface SkillTemplateListParams {
  active?: string;
  skill_type?: string;
  execution_mode?: string;
}

export const skillTemplateService = {
  async list(params?: SkillTemplateListParams): Promise<ApiResponse<JsonApiResource<SkillTemplate>[]>> {
    const queryParams: Record<string, string> = {};
    if (params?.active) queryParams.active = params.active;
    if (params?.skill_type) queryParams.skill_type = params.skill_type;
    if (params?.execution_mode) queryParams.execution_mode = params.execution_mode;

    return apiClient.get('/api/admin/skill_templates', queryParams);
  },

  async get(id: string): Promise<ApiResponse<JsonApiResource<SkillTemplate>>> {
    return apiClient.get(`/api/admin/skill_templates/${id}`);
  },

  async create(data: SkillTemplateCreateData): Promise<ApiResponse<JsonApiResource<SkillTemplate>>> {
    return apiClient.post('/api/admin/skill_templates', {
      skill_template: data,
    });
  },

  async update(
    id: string,
    data: SkillTemplateUpdateData,
    changeDescription?: string
  ): Promise<ApiResponse<JsonApiResource<SkillTemplate>>> {
    return apiClient.patch(`/api/admin/skill_templates/${id}`, {
      skill_template: data,
      change_description: changeDescription,
    });
  },

  async destroy(id: string): Promise<void> {
    return apiClient.delete(`/api/admin/skill_templates/${id}`);
  },

  async sync(id: string, provider: string = 'anthropic'): Promise<ProviderSyncStatus> {
    return apiClient.post(`/api/admin/skill_templates/${id}/sync`, {
      provider,
    });
  },

  async rollback(id: string, versionNumber: number): Promise<ApiResponse<JsonApiResource<SkillTemplate>>> {
    return apiClient.post(`/api/admin/skill_templates/${id}/rollback`, {
      version_number: versionNumber,
    });
  },

  async getVersions(id: string): Promise<SkillTemplateVersion[]> {
    return apiClient.get(`/api/admin/skill_templates/${id}/versions`);
  },

  async generateTriggers(params: GenerateTriggersParams): Promise<GenerateTriggersResult> {
    return apiClient.post('/api/admin/skill_templates/generate_triggers', params);
  },
};
